
import { NgRedux } from '@ptl/angular-redux-store';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BrandingConfigurationService } from '@ptl/brand';
import { StateStructure } from '@ptl/state';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { PaymentData, PaymentDataInterface, PostSubmissionPaymentDataInterface } from 'src/app/angular-elements/on-exchange-enrollment/interfaces/on-exchange-payment.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { BankFormControlsAsJson, CreditCardFormControlsAsJson, OnExchangeEnrollmentPaymentFormStateStructure, ON_EXCHANGE_PAYMENT_METHODS } from 'src/app/angular-elements/shared/interfaces/on-exchange-enrollment-payment-form.interface';
import { ON_EXCHANGE_ENROLLMENT_PAYMENT_FORM_REDUCER_NAME, setPaymentManagerServiceConfirmationNum, setPaymentManagerServiceData } from 'src/app/shared/reducers/on-exchange-enrollment-payment-form.reducer';

export interface SuccessStatus { success: string; code: number | string; }
export interface ErroredStatus { error: string; code: number | string; }

/** Successfully hitting the service with good data */
interface SuccessfulPaymentResponse {
  messageVersion: string; // '001';
  paymentID: string; // 'EXIAA001136233';
  paymentStatus: string; // 'SUCCESS';
  requestType: string; // 'authorize';
}
/** Successfully hitting the service with bad data */
interface ErrorPaymentResponse {
  paymentID: unknown;
  paymentStatus: 'ERROR' | string;
  errors?: {
    error: {
      code: 'B13' | '302' | string;
      message: string;
    }
  };
}
interface PaymentSubmissionResponse<T> {
  baseMessage: {
    returnCode: number
  };
  payload: {
    paymentResponse: T;
  };
}
/** Need a payment component
 * 1) radio buttons
 * 2) ( telesales agents get pay by phone )
      {label: "PAY DIRECTLY FROM MY BANK ACCOUNT", value: "ach", description: achDescription},
      {label: "CHARGE MY CREDIT / DEBIT CARD (Including Pre-Paid Debit Cards)", value: "credit", description: ccDescription},
      {label: "SEND ME A BILL", value: "BML", description: bmlDescription},
      {label: "PAY BY PHONE", value: "pbp", description: pbpDescription}

      form-component-entry name is 'marketplacePaymentOptions'
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentManagerService {

  /** This data is not stored in state currently, a relic of DOJO - so long as this is used within the forms as JSON, no issue when restarting flow  */
  _payment: PaymentDataInterface;
  _applicationId: unknown;
  _merchantName: string;

  _paymentConfirmNum: string;

  constructor(
    private brandService: BrandingConfigurationService,
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>
  ) {
    this.getFromState();
  }

  saveToState() {
    this.ngRedux.dispatch(setPaymentManagerServiceData(this._payment));
    this.ngRedux.dispatch(setPaymentManagerServiceConfirmationNum(this._paymentConfirmNum));
  }
  getFromState() {
    const onxPaymentState: OnExchangeEnrollmentPaymentFormStateStructure
    = this.reducerUtilityService.getPayloadStates()[ON_EXCHANGE_ENROLLMENT_PAYMENT_FORM_REDUCER_NAME];

    if (onxPaymentState.paymentManagerServiceData) {
      this._payment = onxPaymentState.paymentManagerServiceData;
      this._paymentConfirmNum = onxPaymentState.paymentManagerServiceConfirmationNum;
    }
  }
  createPaymentOnX(indResponsibility: string, TransactionId: unknown) {
    const payment = new PaymentData();

    payment.pmtAmt = Number(indResponsibility);
    payment.invoiceNum = '';
    payment.accountNum = String(TransactionId); // For On Exchange this needes to be the Payment Transaction ID generated in the enrollment

    this._payment = payment;
    this.saveToState();
  }

  setPaymentOption(
    paymentObject: { paymentOptions: ON_EXCHANGE_PAYMENT_METHODS },
    obj: {
      merchantName: string,
      accountNum: unknown,
      applicationId?: unknown // I think this is OFFX and deprecated with ebill, leave for lift-n-shift
    }
  ) {
      this._payment.accountNum = String(obj.accountNum);
      this._payment.createBy = 'SHOPX'; // ShopX?
      this._applicationId = obj.applicationId;
      this._merchantName = obj.merchantName;
      this._payment.pmtMethodType = paymentObject.paymentOptions;
      this.saveToState();
  }

  getPaymentOption() {
    if (this._payment && this._payment.pmtMethodType) {
      return this._payment.pmtMethodType;
    }

    return null;
  }
  setPaymentData(
    creditCardFormValues: CreditCardFormControlsAsJson,
    bankFormValues: BankFormControlsAsJson
  ) {

    let payload;

    if (this.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD) {

      payload = {
        ccType: creditCardFormValues.ccType,
        ccNumEnc: creditCardFormValues.ccNumEnc,
        ccExpDt: creditCardFormValues.ccExpDtMonth + creditCardFormValues.ccExpDtYear.substr(2, 3)
      };

      this._payment.cardInfo = payload;
      this._payment.billingAddress = {
        addr1: creditCardFormValues.billingAddress.addressLine1,
        addr2: creditCardFormValues.billingAddress.addressLine2,
        city: creditCardFormValues.billingAddress.city,
        zip: creditCardFormValues.billingAddress.zipcode,
        state: creditCardFormValues.billingAddress.state,
        country: 'US',
        name: creditCardFormValues.name
      };

    } else if (this.getPaymentOption() === ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT) {

      payload = {
        custSubBankID: bankFormValues.routingNumber,
        custSubAcctNumEnc: bankFormValues.accountNumber
      };

      this._payment.achInfo = payload;
      this._payment.billingAddress = {
        addr1: bankFormValues.billingAddress.addressLine1,
        addr2: bankFormValues.billingAddress.addressLine2,
        city: bankFormValues.billingAddress.city,
        zip: bankFormValues.billingAddress.zipcode,
        state: bankFormValues.billingAddress.state,
        country: 'US',
        name: bankFormValues.primaryNameOnAccount
      };
    }

    this.saveToState();

  }

  getPaymentData() {
    return this._payment;
  }

  getPaymentConfirmNum(): string {
    return this._paymentConfirmNum;
  }
  _setPaymentId(response: PaymentSubmissionResponse<SuccessfulPaymentResponse>) {
    if (response.payload.paymentResponse && response.payload.paymentResponse.paymentID) {
        (this._payment as PostSubmissionPaymentDataInterface).paymentId = response.payload.paymentResponse.paymentID;
        this._paymentConfirmNum = response.payload.paymentResponse.paymentID;
        this.saveToState();
    }
  }

  maskPayment(type: ON_EXCHANGE_PAYMENT_METHODS) {
    // default to credit if type is undefined
    let accountNumber;
    let maskedAccountNumber;
    let mask = '';

    if (type && type === ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT) {
      accountNumber = this.getPaymentData().achInfo.custSubAcctNumEnc;
    } else {
      // hits if type is undefined or if type == "credit"
      accountNumber = this.getPaymentData().cardInfo.ccNumEnc;
    }

    const stringLength = accountNumber.length;

    if (stringLength > 4) {
      for (let i = 0; i < (stringLength - 4); i++) {
        mask += '*';
      }
      maskedAccountNumber = mask += accountNumber.substring(stringLength - 4, stringLength);
    } else {
      maskedAccountNumber = '****';
    }

    return maskedAccountNumber;

  }
  toJsonOnX(prospectId: string): string {

    const payloadObj = {
      applicationID: prospectId,
      onExchange: true,
      merchantName: this._merchantName,
      paymentRequest: {
        paymentData: this._payment
      }
    };

    payloadObj.paymentRequest.paymentData.pmtAmt = Number(payloadObj.paymentRequest.paymentData.pmtAmt).toFixed(2);

    if (!this._payment.billingAddress.name) {
       this._payment.billingAddress.name = 'BML';
    }

    const data = {
      baseMessage: {
          headerVersion: '1.0'
      },
      payload: payloadObj
    };

    this.saveToState();
    return JSON.stringify(data);
  }
  submitApplicationOnX(prospectId: string) {
    const url = '/cmcrst/x-services/enroll/submitPayment/onexchange';
    const postData = this.toJsonOnX(prospectId);
    return this._submitApplication(url, postData);
  }
  async _submitApplication(url: string, postData: string): Promise<SuccessStatus | ErroredStatus> {

    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: url,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: postData
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    this.httpClient.disableErrorSnackbar(requestConfigImplementation);
    const response = await this.httpClient.makeRequest<string | HttpErrorResponse>(requestConfigImplementation);

    if (!(response instanceof HttpErrorResponse)) {

      let returnedResponse: PaymentSubmissionResponse<SuccessfulPaymentResponse | ErrorPaymentResponse>;
      if (typeof response === 'object') {
        returnedResponse = response;
      } else {
        returnedResponse = JSON.parse(response);
      }


      if (this._payment.pmtMethodType === 'BML') {
          if (returnedResponse.baseMessage.returnCode === 200) {
              return { success: 'Payment has processed.', code: 200 };
          } else {
              return { error: 'Payment could not be submitted', code: 201 };
          }
      } else {
          if (returnedResponse.payload.paymentResponse.paymentStatus === 'ERROR') {
              if ((returnedResponse.payload.paymentResponse as ErrorPaymentResponse).errors.error.code === '302') {
                  return { error: 'Insufficient Funds', code: 302};
              } else if ((returnedResponse.payload.paymentResponse as ErrorPaymentResponse).errors.error.code === 'B13') {
                  // EBILL FRIENDLY ERROR FOR INVALID ROUTING NUMBER
                  return { error: 'Invalid Routing Number. Re-enter and try again', code: 'B13'};
              } else if ((returnedResponse.payload.paymentResponse as ErrorPaymentResponse).errors.error.code === 'B32') {
                  return { error: 'You must enter a valid CC Expiration Date. (MMYY)', code: 'B23'};
              } else if ((returnedResponse.payload.paymentResponse as ErrorPaymentResponse).errors.error.code === '834') {
                  return { error: 'Unauthorized User', code: '834' };
              } else {
                  return { error: (returnedResponse.payload.paymentResponse as ErrorPaymentResponse).errors.error.message, code: 201 };
              }
          } else {
              this._setPaymentId(returnedResponse as PaymentSubmissionResponse<SuccessfulPaymentResponse>);
              return { success: 'Payment has processed.', code: 200 };
          }
      }
    } else {

      if (response.status === 404 || response.status === 500) {
        return { error: 'Services are down.', code: 404 };
      } else {
        return { error: 'Payment could not be submitted', code: 201 };
      }

    }
  }
}
